import FetchHelper from '../helpers/fetch_helper';
import ProposalHelper from '../helpers/proposal_helper';

const FinancingMethodsDispatcher = {
  clearFinancingMethodCache() {
    this.setStoreVal('financingMethodCache', {});
  },

  clearFinancingMethod() {
    this.dispatch({ type: 'clearFinancingMethodCache' });
    this.setStoreVal('financingMethod', null);
  },

  async fetchFinancingMethod() {
    const customerId = this.getStoreVal('proposal')?.customerId;
    const response = await FetchHelper.fetchJson(`customers/${customerId}/financing-methods`, {
      method: 'get',
      version: '/v3',
    });
    let fm = response.results?.find((e) => e.isPrimary);
    this.setStoreValWithCheck('financingMethod', fm);
    this.setStoreValWithCheck('lenderCode', fm.lenderCode);
    return fm;
  },

  async getFinancingMethod({ proposalId, id }) {
    let proposal = this.getStoreVal('proposal');

    if (id === undefined) {
      if (!proposal) {
        await this.dispatch({ type: 'fetchProposal', data: proposalId });
        proposal = this.getStoreVal('proposal');
      }
      const financingMethod =
        this.getStoreVal('financingMethod') ||
        ProposalHelper.determineFinancingMethodToFetch(proposal, this.getStoreVal('view'));
      if (financingMethod) {
        id = financingMethod.id;
      }
    }

    if (id === undefined) {
      return this.dispatch({ type: 'setErrorMessages', data: ['Financing method ID not specified'] });
    }

    return this.dispatch({
      type: 'fetchFinancingMethod',
      data: { proposalId, id, customerId: this.getStoreVal('view')?.customerId ?? proposal?.customerId },
    }).then(
      (financingMethod) => {
        this.acquireLock('addArray');
        this.dispatch({
          type: 'setFinancingMethod',
          data: financingMethod,
        });
      },
      (error) => {
        this.dispatch({
          type: 'handleRequestError',
          data: { error, defaultErrorMessage: `Could not get Financing Method ${id} of Proposal ${proposalId}` },
        });
      }
    );
  },

  async backgroundGetFinancingMethod({ proposalId, id }) {
    let financingMethodCache = this.getStoreVal('financingMethodCache');
    let cachedFinancingMethod = financingMethodCache[id];

    // only continue if financing method is not in cache AND it's not already being requested
    if (!cachedFinancingMethod && !(financingMethodCache.promises && financingMethodCache.promises[id])) {
      const promise = this.dispatch({ type: 'fetchFinancingMethod', data: { proposalId, id } }).then(
        (financingMethod) => {
          // get cache again in case it changed during async fetch call
          financingMethodCache = this.getStoreVal('financingMethodCache');

          // if promise isn't in store anymore, cache has been emptied and/or another fetch has begun to take
          // place, so discard result
          if (financingMethodCache.promises && financingMethodCache.promises[id] === promise) {
            // insert retrieved financing method into cache
            // get rid of this promise to mark it as no longer in-progress
            this.setStoreVal('financingMethodCache', {
              ...financingMethodCache,
              [id]: financingMethod,
              promises: {
                ...financingMethodCache.promises,
                [id]: undefined,
              },
            });
          }

          return financingMethod;
        },
        () => {
          // get cache again in case it changed during async fetch call
          financingMethodCache = this.getStoreVal('financingMethodCache');

          // if promise isn't in store anymore, cache has been emptied and/or another fetch has begun to take
          // place, so don't bother clearing out rejected promise
          if (financingMethodCache.promises && financingMethodCache.promises[id] === promise) {
            // get rid of this promise to mark it as no longer in-progress
            this.setStoreVal('financingMethodCache', {
              ...financingMethodCache,
              promises: {
                ...financingMethodCache.promises,
                [id]: undefined,
              },
            });
          }
        }
      );

      // immediately add promise to financingMethodCache promises
      this.setStoreVal('financingMethodCache', {
        ...financingMethodCache,
        promises: {
          ...financingMethodCache.promises,
          [id]: promise,
        },
      });

      return promise;
    }

    return true;
  },

  setFinancingMethod(financingMethod) {
    // const financingMethodCache = this.getStoreVal('financingMethodCache');
    // this.setStoreVal('financingMethodCache', {
    //   ...financingMethodCache,
    //   [financingMethod.id]: financingMethod,
    // });
    if (financingMethod) {
      this.setStoreVal('financingMethod', financingMethod);
    } else {
      financingMethod = this.getStoreVal('financingMethod');
    }
    this.releaseLock('addArray');

    const proposal = this.getStoreVal('proposal');
    if (proposal && proposal.financingMethods) {
      // load all other financingMethods in the background
      proposal.financingMethods.forEach((lfm) => {
        if (lfm.id !== financingMethod.id) {
          this.dispatch({ type: 'backgroundGetFinancingMethod', data: { proposalId: proposal.id, id: lfm.id } });
        }
      });
    }
  },

  setFinancingMethodToFetch(lightFinancingMethod) {
    this.setStoreVal('financingMethod', lightFinancingMethod);
  },

  async refreshFinancingMethods() {
    this.dispatch({ type: 'clearFinancingMethodCache' });

    const proposal = this.getStoreVal('proposal');
    const financingMethod = this.getStoreVal('financingMethod');

    if (proposal && financingMethod) {
      await this.dispatch({ type: 'getFinancingMethod', data: { proposalId: proposal.id, id: financingMethod.id } });
    }
  },

  async fetchFinancingProductElements(financingMethodId) {
    let promise;
    try {
      promise = await FetchHelper.fetchJson(`financing-methods/${financingMethodId}/product-elements`, {
        method: 'get',
      });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not retrieve financing request' },
      });
    }
    this.setStoreVal('productElements', promise.results);
    return promise;
  },
};

export default FinancingMethodsDispatcher;
